// Auto-generated industries data — DO NOT EDIT MANUALLY
// Slugs: auto-dealerships, home-services, insurance, restaurants-food
import type { Industry } from '../types';

export const industriesGroup2: Industry[] = [
  {
    "slug": "auto-dealerships",
    "name": "Auto Dealerships",
    "icon": "Car",
    "tagline": "More Test Drives. More Deals. Less Manual Follow-Up.",
    "description": "BizRnR gives auto dealerships an AI sales assistant that responds to every online lead instantly and schedules test drives.",
    "longDescription": "Auto dealerships that respond to leads within 5 minutes are 4x more likely to close the sale, yet the average dealership response time is over 3 hours. In a market where consumers submit inquiries to multiple dealers simultaneously, the first to call back wins. BizRnR gives your dealership an unfair advantage by calling every internet lead within seconds of submission.\n\nBizRnR's AI sales agent engages prospects with natural conversation about the vehicle they inquired about, confirms their trade-in situation, budget range, and financing needs, then books a test drive appointment. The AI is trained on your current inventory and can answer questions about specific makes, models, trim levels, and pricing.\n\nFor service departments, BizRnR handles appointment scheduling, recall notifications, and service follow-ups. When a customer's vehicle triggers a maintenance reminder, BizRnR proactively calls to schedule the service — filling your bays during slower periods.\n\nBDC (Business Development Center) operations see the biggest transformation. Instead of a team of 5-8 BDC reps making manual calls, BizRnR handles the entire outbound campaign — from internet lead response to sold customer follow-up to service retention calls. Dealerships using BizRnR typically reduce BDC staffing costs by 60% while increasing appointment show rates by 35%.\n\nBizRnR integrates with DealerSocket, VinSolutions, CDK Global, and other major DMS/CRM platforms.",
    "painPoints": [
      {
        "title": "Internet Leads Go Cold Fast",
        "description": "Auto buyers contact multiple dealers simultaneously. First response wins."
      },
      {
        "title": "BDC Team Overwhelmed",
        "description": "Your BDC cannot call every internet lead fast enough."
      },
      {
        "title": "Low Show Rate on Appointments",
        "description": "Customers book test drives and ghost without follow-up."
      }
    ],
    "solutions": [
      {
        "title": "Instant Lead Response",
        "description": "BizRnR contacts every internet lead within 60 seconds."
      },
      {
        "title": "AI Trade-In Qualification",
        "description": "AI collects vehicle details before the customer walks in."
      },
      {
        "title": "Appointment Confirmation Sequences",
        "description": "Multi-touch SMS and voice sequences ensure high show rates."
      }
    ],
    "stats": [
      {
        "value": "60s",
        "label": "Average lead response"
      },
      {
        "value": "38%",
        "label": "Higher show rate"
      },
      {
        "value": "5.2x",
        "label": "ROI on internet leads"
      }
    ],
    "useCases": [
      {
        "title": "Saturday Night Web Lead",
        "description": "Buyer submits interest at 9pm. BizRnR calls within 60 seconds and books a Sunday test drive."
      },
      {
        "title": "Trade-In Pre-Qualification",
        "description": "AI calls appointments to collect trade-in info before arrival."
      }
    ],
    "faqs": [
      {
        "question": "Does BizRnR work with my DMS?",
        "answer": "Yes, integrates with major dealer management systems."
      },
      {
        "question": "Can it handle inventory questions?",
        "answer": "Yes, connect your inventory feed and AI answers availability questions."
      },
      {
        "question": "What about price negotiations?",
        "answer": "BizRnR routes pricing conversations to your sales team."
      },
      {
        "question": "Does it work for new and used?",
        "answer": "Yes, configure separate flows for each department."
      }
    ],
    "keywords": [
      "auto dealership AI",
      "car dealership lead automation",
      "BDC automation software"
    ]
  },
  {
    "slug": "home-services",
    "name": "Home Services",
    "icon": "HouseLine",
    "tagline": "Book More Jobs Without Answering Every Call Yourself",
    "description": "BizRnR automates lead response and job booking for painters, electricians, handymen, and more.",
    "longDescription": "Home service businesses — from electricians and handymen to general contractors and painters — live and die by the phone. When a homeowner needs a repair, they call 2-3 companies and hire the first one that answers and sounds professional. BizRnR ensures your home service company is always that first responder.\n\nBizRnR's AI handles the entire customer intake: gathering the homeowner's name, address, description of the job, preferred scheduling, and access instructions. For emergency calls (burst pipes, electrical fires, broken locks), the AI immediately dispatches your on-call technician while keeping the customer on the line. For standard jobs, it books an estimate appointment and sends a confirmation text with your company details.\n\nQuote follow-up is where home service companies lose the most revenue. After you spend an hour at a customer's home writing up an estimate, BizRnR follows up automatically at 24 hours, 3 days, and 7 days — converting estimates that would otherwise go cold. This alone typically recovers 20-30% of previously lost revenue.\n\nSeasonal campaigns drive additional growth: BizRnR calls your customer database before gutter cleaning season, before holiday lighting installation, or after storms for damage assessment and repair scheduling.\n\nFor growing home service companies, BizRnR scales instantly — handling 1 call or 100 simultaneous calls with the same quality. No more hiring and training seasonal phone staff.",
    "painPoints": [
      {
        "title": "Too Busy to Answer Calls",
        "description": "When on the job, leads call your competitors."
      },
      {
        "title": "Quote Requests Pile Up",
        "description": "You come home to 10 voicemails with no context."
      },
      {
        "title": "Seasonal Demand Spikes",
        "description": "Spring projects flood your inbox and you cannot scale manually."
      }
    ],
    "solutions": [
      {
        "title": "Always-On AI Receptionist",
        "description": "BizRnR answers every call and collects job details while you work."
      },
      {
        "title": "Lead Prioritization",
        "description": "AI scores and routes leads by job size, urgency, and location."
      },
      {
        "title": "Automated Quote Follow-Up",
        "description": "Never let a quote go cold with automatic follow-up."
      }
    ],
    "stats": [
      {
        "value": "100%",
        "label": "Calls answered"
      },
      {
        "value": "3.5x",
        "label": "More jobs booked"
      },
      {
        "value": "28%",
        "label": "Higher quote close rate"
      }
    ],
    "useCases": [
      {
        "title": "Painter on the Job Site",
        "description": "BizRnR answers new inquiries and books estimates for next week."
      },
      {
        "title": "Spring Cleaning Rush",
        "description": "BizRnR manages the seasonal surge without manual intervention."
      }
    ],
    "faqs": [
      {
        "question": "Do I need technical skills to set up?",
        "answer": "No, setup takes under 30 minutes with our onboarding wizard."
      },
      {
        "question": "Can I set my service area?",
        "answer": "Yes, BizRnR declines leads outside your area automatically."
      },
      {
        "question": "What if I am fully booked?",
        "answer": "AI informs callers of your next available slot."
      },
      {
        "question": "Can it handle multiple services?",
        "answer": "Yes, configure separate flows for each service."
      }
    ],
    "keywords": [
      "home services AI",
      "handyman lead automation",
      "contractor answering service"
    ]
  },
  {
    "slug": "insurance",
    "name": "Insurance",
    "icon": "ShieldCheck",
    "tagline": "Quote More. Bind More. Automate the Follow-Up.",
    "description": "BizRnR helps insurance agents respond to quote requests instantly and reactivate lapsed policyholders automatically.",
    "longDescription": "Insurance agencies face a unique challenge: policy renewals, claims intake, and new quote requests all compete for phone time, and missing any of them has direct revenue consequences. BizRnR gives insurance agencies an AI phone agent that handles all three simultaneously, ensuring no prospect or policyholder is ever sent to voicemail.\n\nFor new business, BizRnR responds to quote requests instantly. The AI collects the prospect's coverage needs, current carrier, policy expiration date, and contact information, then schedules a consultation with the appropriate agent or provides a preliminary quote range. Agencies using BizRnR see 2.8x more quote appointments booked because the AI responds in seconds rather than hours.\n\nClaims intake is streamlined through BizRnR's structured interview process. When a policyholder calls to report an accident or property damage, the AI calmly guides them through the details — date, location, description, other parties involved, police report number — and creates a complete first notice of loss that goes directly into your agency management system.\n\nPolicy renewal campaigns are fully automated: BizRnR calls policyholders 60, 30, and 14 days before expiration, confirms their intent to renew, and identifies cross-sell opportunities. This proactive retention approach reduces policy lapse rates by up to 23%.\n\nBizRnR integrates with Applied Epic, AMS360, HawkSoft, and other insurance agency management systems.",
    "painPoints": [
      {
        "title": "Quote Requests Ignored",
        "description": "Web leads expect response in minutes. Delays send them to competitors."
      },
      {
        "title": "Open Quotes Expire",
        "description": "Agents send quotes and never follow up."
      },
      {
        "title": "Policy Renewal Attrition",
        "description": "Clients lapse because no one called at renewal time."
      }
    ],
    "solutions": [
      {
        "title": "Instant Quote Response",
        "description": "AI contacts every new quote request within 60 seconds."
      },
      {
        "title": "Open Quote Follow-Up",
        "description": "Multi-touch sequences on every open quote."
      },
      {
        "title": "Renewal Retention Campaigns",
        "description": "BizRnR contacts policyholders 60, 30, and 7 days before renewal."
      }
    ],
    "stats": [
      {
        "value": "4.7x",
        "label": "More quotes converted"
      },
      {
        "value": "61%",
        "label": "Higher retention rate"
      },
      {
        "value": "90s",
        "label": "Average quote response"
      }
    ],
    "useCases": [
      {
        "title": "Weekend Web Lead",
        "description": "Prospect requests a quote Saturday. BizRnR calls within 90 seconds and schedules follow-up."
      },
      {
        "title": "Renewal Campaign",
        "description": "30 days before renewal, BizRnR calls every policyholder to confirm coverage."
      }
    ],
    "faqs": [
      {
        "question": "Is BizRnR compliant with insurance regulations?",
        "answer": "Consult your compliance officer for state-specific requirements."
      },
      {
        "question": "Can it handle multiple insurance lines?",
        "answer": "Yes, configure separate flows for auto, home, life, and more."
      },
      {
        "question": "Does it integrate with insurance CRMs?",
        "answer": "Yes, integrates with major platforms via API."
      },
      {
        "question": "Can the AI quote rates?",
        "answer": "BizRnR can read from your rate sheet or hand off to your quoting system."
      }
    ],
    "keywords": [
      "insurance lead automation",
      "insurance AI voice",
      "policy renewal automation"
    ]
  },
  {
    "slug": "restaurants-food",
    "name": "Restaurants & Food",
    "icon": "ForkKnife",
    "tagline": "More Reservations. More Catering. Less Phone Chaos.",
    "description": "BizRnR handles restaurant reservations, catering inquiries, and callbacks automatically so your staff can focus on service.",
    "longDescription": "Restaurants and food service businesses miss an average of 30% of incoming phone calls during peak hours. When customers call to place a takeout order, make a reservation, or inquire about catering, and nobody answers, they simply call the next restaurant. BizRnR ensures your restaurant never misses a revenue opportunity by answering every call instantly with an AI that knows your menu, hours, and specials.\n\nBizRnR's AI handles reservations by checking your real-time availability, confirming party size and special requests (dietary restrictions, high chairs, private dining), and sending instant text confirmations. For takeout orders, the AI walks customers through your menu, handles modifications, confirms pricing, and routes the order to your kitchen display system.\n\nCatering and event inquiries are captured professionally even during your busiest dinner service. The AI collects event details — date, guest count, budget, dietary requirements — and schedules a callback from your catering manager during business hours. No more losing $5,000 catering gigs because you were too busy plating entrées to answer the phone.\n\nFor restaurant groups managing multiple locations, BizRnR routes calls to the correct location based on caller area code or direct inquiry, maintaining separate menus and availability for each.\n\nAutomated reservation confirmations and reminders reduce no-shows by up to 40%, directly impacting your bottom line.",
    "painPoints": [
      {
        "title": "Phone Rings Disrupt Service",
        "description": "Staff answering phones during a rush means slower service."
      },
      {
        "title": "Catering Leads Not Followed Up",
        "description": "Large catering inquiries get buried, costing thousands."
      },
      {
        "title": "Reservation No-Shows",
        "description": "Tables sit empty because confirmations were never sent."
      }
    ],
    "solutions": [
      {
        "title": "AI Phone Host",
        "description": "BizRnR answers reservation calls and books tables automatically."
      },
      {
        "title": "Catering Lead Response",
        "description": "Large event inquiries get instant AI follow-up with pricing."
      },
      {
        "title": "Reservation Reminders",
        "description": "Automated SMS confirmations reduce no-shows."
      }
    ],
    "stats": [
      {
        "value": "85%",
        "label": "Calls handled by AI"
      },
      {
        "value": "52%",
        "label": "Fewer no-shows"
      },
      {
        "value": "3.1x",
        "label": "Catering inquiry conversion"
      }
    ],
    "useCases": [
      {
        "title": "Friday Night Rush",
        "description": "While at capacity, BizRnR handles all reservation calls."
      },
      {
        "title": "Corporate Catering Inquiry",
        "description": "A company inquires about a 200-person party. BizRnR responds instantly."
      }
    ],
    "faqs": [
      {
        "question": "Does BizRnR integrate with OpenTable?",
        "answer": "Yes, connects via API or webhook."
      },
      {
        "question": "Can the AI handle special requests?",
        "answer": "Yes, captures dietary restrictions and special occasions."
      },
      {
        "question": "What languages are supported?",
        "answer": "English by default with multilingual options available."
      },
      {
        "question": "Can I customize the personality?",
        "answer": "Yes, match your restaurant tone from casual to fine dining."
      }
    ],
    "keywords": [
      "restaurant AI receptionist",
      "reservation automation",
      "catering lead management"
    ]
  }
];
